﻿// BookPreview.js
import React, { useState, useEffect, useRef } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  SafeAreaView,
  StatusBar,
  ScrollView,
  Image,
  Dimensions,
  Share,
  Linking,
  Alert,
  Platform,
  Animated,
  Modal,
  TextInput,
} from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { 
  MaterialCommunityIcons, 
  Ionicons, 
  FontAwesome5, 
  Feather,
  AntDesign 
} from '@expo/vector-icons';
import { useNavigation, useRoute } from '@react-navigation/native';
import { LinearGradient } from 'expo-linear-gradient';
import * as FileSystem from 'expo-file-system';
import * as Sharing from 'expo-sharing';
import { WebView } from 'react-native-webview';

const { width, height } = Dimensions.get('window');

const BookPreview = () => {
  const navigation = useNavigation();
  const route = useRoute();
  const { book } = route.params || {};

  // Coerce any field to a string array: handles real arrays, comma-separated
  // strings (how the DB stores topics), JSON-string arrays, or null/undefined.
  const toArray = (val) => {
    if (Array.isArray(val)) return val;
    if (typeof val === 'string') {
      const s = val.trim();
      if (!s) return [];
      if (s.startsWith('[')) { try { const p = JSON.parse(s); if (Array.isArray(p)) return p; } catch {} }
      return s.split(',').map((x) => x.trim()).filter(Boolean);
    }
    return [];
  };
  
  const [loading, setLoading] = useState(true);
  const [darkMode, setDarkMode] = useState(false);
  const [isBookmarked, setIsBookmarked] = useState(false);
  const [rating, setRating] = useState(book?.rating || 0);
  const [userReview, setUserReview] = useState('');
  const [reviews, setReviews] = useState([]);
  const [showFullDescription, setShowFullDescription] = useState(false);
  const [selectedChapter, setSelectedChapter] = useState(null);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [showDownloadOptions, setShowDownloadOptions] = useState(false);
  const [animation] = useState(new Animated.Value(0));
  
  const scrollViewRef = useRef();
  const fadeAnim = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    loadInitialData();
    startAnimations();
  }, []);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      
      // Load dark mode preference
      const darkModePref = await AsyncStorage.getItem('darkMode');
      if (darkModePref !== null) {
        setDarkMode(JSON.parse(darkModePref));
      }
      
      // Load bookmarks
      const bookmarks = await AsyncStorage.getItem('bookmarkedBooks');
      if (bookmarks) {
        const bookmarkedBooks = JSON.parse(bookmarks);
        setIsBookmarked(bookmarkedBooks.includes(book?.id));
      }
      
      // Load user reviews
      const storedReviews = await AsyncStorage.getItem(`reviews_${book?.id}`);
      if (storedReviews) {
        setReviews(JSON.parse(storedReviews));
      }
      
      // Load user rating
      const userRating = await AsyncStorage.getItem(`rating_${book?.id}`);
      if (userRating) {
        setRating(parseFloat(userRating));
      }
      
      // Simulate loading delay
      setTimeout(() => {
        setLoading(false);
      }, 800);
      
    } catch (error) {
      console.error('Error loading initial data:', error);
      setLoading(false);
    }
  };

  const startAnimations = () => {
    Animated.parallel([
      Animated.timing(fadeAnim, {
        toValue: 1,
        duration: 800,
        useNativeDriver: true,
      }),
      Animated.timing(animation, {
        toValue: 1,
        duration: 1000,
        useNativeDriver: true,
      }),
    ]).start();
  };

  const toggleBookmark = async () => {
    try {
      const bookmarks = await AsyncStorage.getItem('bookmarkedBooks') || '[]';
      const bookmarkedBooks = JSON.parse(bookmarks);
      
      let newBookmarks;
      if (isBookmarked) {
        newBookmarks = bookmarkedBooks.filter(id => id !== book.id);
        setIsBookmarked(false);
      } else {
        newBookmarks = [...bookmarkedBooks, book.id];
        setIsBookmarked(true);
      }
      
      await AsyncStorage.setItem('bookmarkedBooks', JSON.stringify(newBookmarks));
    } catch (error) {
      console.error('Error toggling bookmark:', error);
    }
  };

  const handleRating = async (newRating) => {
    try {
      setRating(newRating);
      await AsyncStorage.setItem(`rating_${book.id}`, newRating.toString());
      
      // Add to user's reviewed books
      const reviewedBooks = await AsyncStorage.getItem('reviewedBooks') || '[]';
      const reviewedBooksArray = JSON.parse(reviewedBooks);
      if (!reviewedBooksArray.includes(book.id)) {
        reviewedBooksArray.push(book.id);
        await AsyncStorage.setItem('reviewedBooks', JSON.stringify(reviewedBooksArray));
      }
    } catch (error) {
      console.error('Error saving rating:', error);
    }
  };

  const submitReview = async () => {
    if (!userReview.trim()) return;
    
    try {
      const newReview = {
        id: Date.now(),
        text: userReview,
        rating: rating,
        timestamp: new Date().toISOString(),
        user: 'You',
      };
      
      const updatedReviews = [newReview, ...reviews];
      setReviews(updatedReviews);
      setUserReview('');
      setShowReviewModal(false);
      
      await AsyncStorage.setItem(`reviews_${book.id}`, JSON.stringify(updatedReviews));
      Alert.alert('Success', 'Review submitted successfully!');
    } catch (error) {
      console.error('Error submitting review:', error);
      Alert.alert('Error', 'Failed to submit review');
    }
  };

  const shareBook = async () => {
    try {
      await Share.share({
        title: `📚 ${book.title}`,
        message: `Check out this engineering book: "${book.title}" by ${book.author}\n\n${book.description}\n\nRating: ${book.rating}/5\nAvailable on: ${book.amazonLink}`,
      });
    } catch (error) {
      console.error('Error sharing book:', error);
    }
  };

  const openAmazonLink = async () => {
    try {
      const amazon = book?.amazonLink || book?.amazon_link;
      if (!amazon) {
        Alert.alert('Not available', 'No purchase link is available for this book yet.');
        return;
      }
      await Linking.openURL(amazon);
    } catch (error) {
      console.error('Error opening Amazon:', error);
      Alert.alert('Error', 'Failed to open Amazon');
    }
  };

  const openPreview = (chapter = null) => {
    const realPdf = book?.pdfUrl || book?.pdf_url;
    const isFree = book?.isFree || Number(book?.price) === 0 || book?.pdf_available || !!realPdf;
    // Free book with a real PDF → open the full reader directly.
    if (isFree && realPdf && !chapter) {
      navigation.navigate('PDFViewer', { book });
      return;
    }
    setSelectedChapter(chapter);
    setShowPreviewModal(true);
  };

  const downloadBook = async (format = 'pdf') => {
    try {
      setShowDownloadOptions(false);
      const realPdf = book?.pdfUrl || book?.pdf_url;
      const amazon = book?.amazonLink || book?.amazon_link;

      if (realPdf) {
        // Free/openly-licensed book with a real PDF — open it in the reader.
        navigation.navigate('PDFViewer', { book });
      } else if (amazon) {
        // Paid book — send to Amazon to purchase (affiliate link).
        await Linking.openURL(amazon);
      } else {
        Alert.alert(
          'Not available',
          'A readable or purchasable copy isn\u2019t available for this book yet.',
          [{ text: 'OK' }]
        );
      }
    } catch (error) {
      console.error('Error opening book:', error);
      Alert.alert('Error', 'Could not open this book. Please try again.');
    }
  };

  const _legacyDownloadBook = async (format = 'pdf') => {
    try {
      setShowDownloadOptions(false);
      
      let downloadUrl;
      switch (format) {
        case 'pdf':
          downloadUrl = book.pdfUrl || `https://example.com/${book.id}.pdf`;
          break;
        case 'epub':
          downloadUrl = `https://example.com/${book.id}.epub`;
          break;
        case 'mobi':
          downloadUrl = `https://example.com/${book.id}.mobi`;
          break;
        default:
          downloadUrl = book.pdfUrl;
      }
      
      Alert.alert(
        'Download Started',
        `Downloading ${book.title} in ${format.toUpperCase()} format...`,
        [{ text: 'OK' }]
      );
      
      // Track download in user history
      const downloadHistory = await AsyncStorage.getItem('downloadHistory') || '[]';
      const history = JSON.parse(downloadHistory);
      history.unshift({
        id: book.id,
        title: book.title,
        format: format,
        date: new Date().toISOString(),
      });
      await AsyncStorage.setItem('downloadHistory', JSON.stringify(history.slice(0, 50)));
      
    } catch (error) {
      console.error('Error downloading book:', error);
      Alert.alert('Error', 'Failed to download book');
    }
  };

  const renderStars = (rating, size = 16) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 >= 0.5;
    
    for (let i = 0; i < 5; i++) {
      if (i < fullStars) {
        stars.push(
          <MaterialCommunityIcons key={i} name="star" size={size} color="#fbbf24" />
        );
      } else if (i === fullStars && hasHalfStar) {
        stars.push(
          <MaterialCommunityIcons key={i} name="star-half-full" size={size} color="#fbbf24" />
        );
      } else {
        stars.push(
          <MaterialCommunityIcons key={i} name="star-outline" size={size} color="#fbbf24" />
        );
      }
    }
    
    return stars;
  };

  const renderLoading = () => (
    <View style={[styles.loadingContainer, { backgroundColor: darkMode ? '#1a202c' : '#f0f4f8' }]}>
      <Animated.View style={{ opacity: fadeAnim }}>
        <MaterialCommunityIcons name="book-open-variant" size={64} color="#667eea" />
        <Text style={[styles.loadingText, { color: darkMode ? '#cbd5e0' : '#1f2937' }]}>
          Loading book details...
        </Text>
      </Animated.View>
    </View>
  );

  const renderHeader = () => (
    <LinearGradient
      colors={darkMode ? ['#2d3748', '#4a5568'] : ['#667eea', '#764ba2']}
      style={styles.header}
    >
      <TouchableOpacity 
        style={styles.backButton}
        onPress={() => navigation.goBack()}
      >
        <MaterialCommunityIcons name="arrow-left" size={24} color="#ffffff" />
      </TouchableOpacity>
      
      <Text style={styles.headerTitle} numberOfLines={1}>
        📚 Book Preview
      </Text>
      
      <View style={styles.headerActions}>
        <TouchableOpacity 
          style={styles.iconButton}
          onPress={toggleBookmark}
        >
          <MaterialCommunityIcons
            name={isBookmarked ? "bookmark" : "bookmark-outline"}
            size={24}
            color="#ffffff"
          />
        </TouchableOpacity>
        
        <TouchableOpacity 
          style={styles.iconButton}
          onPress={shareBook}
        >
          <MaterialCommunityIcons
            name="share-variant"
            size={24}
            color="#ffffff"
          />
        </TouchableOpacity>
      </View>
    </LinearGradient>
  );

  const renderBookCover = () => (
    <Animated.View 
      style={[
        styles.bookCoverContainer,
        {
          opacity: fadeAnim,
          transform: [{
            translateY: animation.interpolate({
              inputRange: [0, 1],
              outputRange: [50, 0],
            }),
          }],
        },
      ]}
    >
      <LinearGradient
        colors={[book?.color || '#667eea', `${book?.color || '#667eea'}80`]}
        style={styles.bookCoverGradient}
      >
        <View style={styles.bookCoverContent}>
          <MaterialCommunityIcons 
            name={book?.icon || 'book-open-variant'} 
            size={80} 
            color="#ffffff" 
          />
          <Text style={styles.bookCoverTitle} numberOfLines={2}>
            {book?.title}
          </Text>
          <Text style={styles.bookCoverAuthor}>
            by {book?.author}
          </Text>
        </View>
      </LinearGradient>
      
      {book?.coverImage && (
        <Image
          source={{ uri: (book.coverImage && typeof book.coverImage=="string" && book.coverImage.startsWith("http")) ? book.coverImage : undefined }}
          style={styles.bookCoverImage}
          resizeMode="cover"
        />
      )}
    </Animated.View>
  );

  const renderBookInfo = () => (
    <View style={[styles.bookInfoContainer, { backgroundColor: darkMode ? '#2d3748' : '#ffffff' }]}>
      <Text style={[styles.bookTitle, { color: darkMode ? '#f7fafc' : '#1f2937' }]}>
        {book?.title}
      </Text>
      
      <Text style={[styles.bookAuthor, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
        by {book?.author}
      </Text>
      
      <View style={styles.bookMeta}>
        <View style={styles.metaItem}>
          <MaterialCommunityIcons name="book-open-page-variant" size={16} color="#667eea" />
          <Text style={[styles.metaText, { color: darkMode ? '#cbd5e0' : '#4b5563' }]}>
            {book?.edition}
          </Text>
        </View>
        
        <View style={styles.metaItem}>
          <MaterialCommunityIcons name="office-building" size={16} color="#667eea" />
          <Text style={[styles.metaText, { color: darkMode ? '#cbd5e0' : '#4b5563' }]}>
            {book?.publisher}
          </Text>
        </View>
        
        <View style={styles.metaItem}>
          <MaterialCommunityIcons name="calendar" size={16} color="#667eea" />
          <Text style={[styles.metaText, { color: darkMode ? '#cbd5e0' : '#4b5563' }]}>
            {book?.year}
          </Text>
        </View>
      </View>
      
      <View style={styles.ratingContainer}>
        <View style={styles.ratingStars}>
          {renderStars(book?.rating || 0, 20)}
          <Text style={[styles.ratingText, { color: darkMode ? '#cbd5e0' : '#4b5563' }]}>
            {book?.rating}/5
          </Text>
        </View>
        
        <View style={styles.priceContainer}>
          <MaterialCommunityIcons name="tag" size={20} color="#10b981" />
          <Text style={styles.priceText}>{book?.price}</Text>
        </View>
      </View>
      
      <View style={styles.isbnContainer}>
        <Text style={[styles.isbnLabel, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
          ISBN: 
        </Text>
        <Text style={[styles.isbnValue, { color: darkMode ? '#cbd5e0' : '#4b5563' }]}>
          {book?.isbn}
        </Text>
      </View>
    </View>
  );

  const renderDescription = () => (
    <View style={[styles.descriptionContainer, { backgroundColor: darkMode ? '#2d3748' : '#ffffff' }]}>
      <Text style={[styles.sectionTitle, { color: darkMode ? '#f7fafc' : '#1f2937' }]}>
        📖 Description
      </Text>
      
      <Text 
        style={[styles.descriptionText, { color: darkMode ? '#cbd5e0' : '#4b5563' }]}
        numberOfLines={showFullDescription ? undefined : 4}
      >
        {book?.description}
      </Text>
      
      {book?.description && book.description.length > 200 && (
        <TouchableOpacity
          onPress={() => setShowFullDescription(!showFullDescription)}
        >
          <Text style={styles.readMoreText}>
            {showFullDescription ? 'Show Less' : 'Read More'}
          </Text>
        </TouchableOpacity>
      )}
    </View>
  );

  const renderTopics = () => (
    <View style={[styles.topicsContainer, { backgroundColor: darkMode ? '#2d3748' : '#ffffff' }]}>
      <Text style={[styles.sectionTitle, { color: darkMode ? '#f7fafc' : '#1f2937' }]}>
        🎯 Key Topics
      </Text>
      
      <View style={styles.topicsList}>
        {toArray(book?.topics).map((topic, index) => (
          <TouchableOpacity
            key={index}
            style={[styles.topicTag, { backgroundColor: darkMode ? '#4a5568' : '#e5e7eb' }]}
            onPress={() => navigation.navigate('TopicSearch', { topic })}
          >
            <Text style={[styles.topicText, { color: darkMode ? '#cbd5e0' : '#4b5563' }]}>
              {topic}
            </Text>
          </TouchableOpacity>
        ))}
      </View>
    </View>
  );

  const renderChapters = () => (
    <View style={[styles.chaptersContainer, { backgroundColor: darkMode ? '#2d3748' : '#ffffff' }]}>
      <Text style={[styles.sectionTitle, { color: darkMode ? '#f7fafc' : '#1f2937' }]}>
        📑 Table of Contents
      </Text>
      
      <Text style={[styles.chaptersSubtitle, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
        {book?.pages} pages • {book?.level}
      </Text>
      
      {(toArray(book?.chapters).length
        ? toArray(book?.chapters)
        : Array.from({ length: 10 }, (_, i) => `Chapter ${i + 1}`)
      ).map((chapter, index) => (
        <TouchableOpacity
          key={index}
          style={[styles.chapterItem, { backgroundColor: darkMode ? '#4a5568' : '#f3f4f6' }]}
          onPress={() => openPreview(chapter)}
        >
          <View style={styles.chapterNumber}>
            <Text style={[styles.chapterNumberText, { color: darkMode ? '#ffffff' : '#1f2937' }]}>
              {index + 1}
            </Text>
          </View>
          
          <View style={styles.chapterContent}>
            <Text style={[styles.chapterTitle, { color: darkMode ? '#ffffff' : '#1f2937' }]}>
              {chapter}
            </Text>
            <Text style={[styles.chapterPages, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
              {Math.floor(book?.pages / 10)} pages
            </Text>
          </View>
          
          <TouchableOpacity onPress={() => openPreview(chapter)}>
            <MaterialCommunityIcons 
              name="eye" 
              size={20} 
              color={darkMode ? '#a0aec0' : '#6b7280'} 
            />
          </TouchableOpacity>
        </TouchableOpacity>
      ))}
      
      <TouchableOpacity
        style={styles.viewAllChapters}
        onPress={() => navigation.navigate('FullTOC', { book })}
      >
        <Text style={styles.viewAllText}>View All Chapters</Text>
        <MaterialCommunityIcons name="chevron-right" size={20} color="#667eea" />
      </TouchableOpacity>
    </View>
  );

  const renderUserRating = () => (
    <View style={[styles.ratingSection, { backgroundColor: darkMode ? '#2d3748' : '#ffffff' }]}>
      <Text style={[styles.sectionTitle, { color: darkMode ? '#f7fafc' : '#1f2937' }]}>
        ⭐ Your Rating
      </Text>
      
      <View style={styles.userRatingContainer}>
        <View style={styles.starsContainer}>
          {[1, 2, 3, 4, 5].map((star) => (
            <TouchableOpacity
              key={star}
              onPress={() => handleRating(star)}
            >
              <MaterialCommunityIcons
                name={star <= rating ? "star" : "star-outline"}
                size={32}
                color={star <= rating ? "#fbbf24" : (darkMode ? "#4a5568" : "#d1d5db")}
              />
            </TouchableOpacity>
          ))}
        </View>
        
        <Text style={[styles.ratingValue, { color: darkMode ? '#cbd5e0' : '#4b5563' }]}>
          {rating}/5
        </Text>
      </View>
      
      <TouchableOpacity
        style={styles.reviewButton}
        onPress={() => setShowReviewModal(true)}
      >
        <MaterialCommunityIcons name="pencil" size={20} color="#ffffff" />
        <Text style={styles.reviewButtonText}>Write a Review</Text>
      </TouchableOpacity>
    </View>
  );

  const renderReviews = () => (
    <View style={[styles.reviewsContainer, { backgroundColor: darkMode ? '#2d3748' : '#ffffff' }]}>
      <View style={styles.reviewsHeader}>
        <Text style={[styles.sectionTitle, { color: darkMode ? '#f7fafc' : '#1f2937' }]}>
          💬 Reviews ({reviews.length + 5})
        </Text>
        
        <TouchableOpacity onPress={() => navigation.navigate('AllReviews', { book })}>
          <Text style={styles.seeAllText}>See All</Text>
        </TouchableOpacity>
      </View>
      
      {reviews.slice(0, 3).map((review) => (
        <View key={review.id} style={[styles.reviewItem, { backgroundColor: darkMode ? '#4a5568' : '#f3f4f6' }]}>
          <View style={styles.reviewHeader}>
            <View style={styles.reviewerInfo}>
              <Text style={[styles.reviewerName, { color: darkMode ? '#ffffff' : '#1f2937' }]}>
                {review.user}
              </Text>
              <View style={styles.reviewStars}>
                {renderStars(review.rating, 14)}
              </View>
            </View>
            <Text style={[styles.reviewDate, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
              {new Date(review.timestamp).toLocaleDateString()}
            </Text>
          </View>
          <Text style={[styles.reviewText, { color: darkMode ? '#cbd5e0' : '#4b5563' }]}>
            {review.text}
          </Text>
        </View>
      ))}
      
      {reviews.length === 0 && (
        <View style={styles.noReviews}>
          <MaterialCommunityIcons name="comment-outline" size={48} color={darkMode ? '#4a5568' : '#d1d5db'} />
          <Text style={[styles.noReviewsText, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
            No reviews yet. Be the first to review!
          </Text>
        </View>
      )}
    </View>
  );

  const renderActionButtons = () => {
    const realPdf = book?.pdfUrl || book?.pdf_url;
    const isFreeBook = book?.isFree === true || Number(book?.price) === 0 || book?.pdf_available === true || !!realPdf;

    return (
      <View style={[styles.actionButtonsContainer, { backgroundColor: darkMode ? '#2d3748' : '#ffffff' }]}>
        {isFreeBook && realPdf ? (
          // FREE book with a real PDF → Read it in the app.
          <TouchableOpacity
            style={[styles.actionButton, styles.previewButton, { backgroundColor: '#16A34A', flex: 1 }]}
            onPress={() => navigation.navigate('PDFViewer', { book })}
          >
            <MaterialCommunityIcons name="book-open-variant" size={24} color="#ffffff" />
            <Text style={styles.actionButtonText}>Read Book</Text>
          </TouchableOpacity>
        ) : (
          // PAID book → Preview + Buy on Amazon.
          <>
            <TouchableOpacity
              style={[styles.actionButton, styles.previewButton]}
              onPress={() => openPreview()}
            >
              <MaterialCommunityIcons name="eye" size={24} color="#ffffff" />
              <Text style={styles.actionButtonText}>Preview</Text>
            </TouchableOpacity>

            <TouchableOpacity
              style={[styles.actionButton, styles.amazonButton]}
              onPress={openAmazonLink}
            >
              <FontAwesome5 name="amazon" size={20} color="#ffffff" />
              <Text style={styles.actionButtonText}>Buy on Amazon</Text>
            </TouchableOpacity>
          </>
        )}
      </View>
    );
  };

  const renderPreviewModal = () => (
    <Modal
      visible={showPreviewModal}
      animationType="slide"
      onRequestClose={() => setShowPreviewModal(false)}
    >
      <SafeAreaView style={[styles.modalContainer, { backgroundColor: darkMode ? '#1a202c' : '#ffffff' }]}>
        <View style={[styles.modalHeader, { backgroundColor: darkMode ? '#2d3748' : '#f3f4f6' }]}>
          <TouchableOpacity onPress={() => setShowPreviewModal(false)}>
            <MaterialCommunityIcons name="close" size={24} color={darkMode ? '#ffffff' : '#1f2937'} />
          </TouchableOpacity>
          
          <Text style={[styles.modalTitle, { color: darkMode ? '#ffffff' : '#1f2937' }]}>
            {selectedChapter ? `Preview: ${selectedChapter}` : 'Book Preview'}
          </Text>
          
          <View style={{ width: 24 }} />
        </View>
        
        {(() => {
          const realPdf = book?.pdfUrl || book?.pdf_url;
          if (realPdf) {
            return (
              <WebView
                source={{ uri: `https://docs.google.com/viewer?url=${encodeURIComponent(realPdf)}&embedded=true` }}
                style={styles.previewWebView}
                startInLoadingState={true}
                scalesPageToFit={true}
              />
            );
          }
          return (
            <View style={[styles.previewWebView, { alignItems: 'center', justifyContent: 'center', padding: 24 }]}>
              <MaterialCommunityIcons name="book-lock-outline" size={56} color="#cbd5e0" />
              <Text style={{ marginTop: 14, fontSize: 15, color: '#6b7280', textAlign: 'center', lineHeight: 22 }}>
                A preview isn't available for this title. Use “Buy on Amazon” to purchase the full book.
              </Text>
            </View>
          );
        })()}
        
        <View style={[styles.previewActions, { backgroundColor: darkMode ? '#2d3748' : '#f3f4f6' }]}>
          <TouchableOpacity 
            style={styles.previewActionButton}
            onPress={() => navigation.navigate('PDFViewer', { book })}
          >
            <MaterialCommunityIcons name="file-pdf-box" size={20} color="#667eea" />
            <Text style={[styles.previewActionText, { color: '#667eea' }]}>Full PDF</Text>
          </TouchableOpacity>
          
          <TouchableOpacity 
            style={styles.previewActionButton}
            onPress={openAmazonLink}
          >
            <FontAwesome5 name="amazon" size={18} color="#FF9900" />
            <Text style={[styles.previewActionText, { color: '#FF9900' }]}>Purchase</Text>
          </TouchableOpacity>
          
          <TouchableOpacity 
            style={styles.previewActionButton}
            onPress={() => setShowDownloadOptions(true)}
          >
            <MaterialCommunityIcons name="download" size={20} color="#10b981" />
            <Text style={[styles.previewActionText, { color: '#10b981' }]}>Download</Text>
          </TouchableOpacity>
        </View>
      </SafeAreaView>
    </Modal>
  );

  const renderReviewModal = () => (
    <Modal
      visible={showReviewModal}
      animationType="slide"
      transparent={true}
      onRequestClose={() => setShowReviewModal(false)}
    >
      <View style={styles.modalOverlay}>
        <View style={[styles.reviewModalContent, { backgroundColor: darkMode ? '#2d3748' : '#ffffff' }]}>
          <View style={styles.reviewModalHeader}>
            <Text style={[styles.reviewModalTitle, { color: darkMode ? '#ffffff' : '#1f2937' }]}>
              Write a Review
            </Text>
            <TouchableOpacity onPress={() => setShowReviewModal(false)}>
              <MaterialCommunityIcons name="close" size={24} color={darkMode ? '#ffffff' : '#1f2937'} />
            </TouchableOpacity>
          </View>
          
          <View style={styles.reviewModalRating}>
            <Text style={[styles.reviewModalLabel, { color: darkMode ? '#cbd5e0' : '#4b5563' }]}>
              Your Rating:
            </Text>
            <View style={styles.reviewModalStars}>
              {[1, 2, 3, 4, 5].map((star) => (
                <TouchableOpacity
                  key={star}
                  onPress={() => setRating(star)}
                >
                  <MaterialCommunityIcons
                    name={star <= rating ? "star" : "star-outline"}
                    size={32}
                    color={star <= rating ? "#fbbf24" : (darkMode ? "#4a5568" : "#d1d5db")}
                  />
                </TouchableOpacity>
              ))}
            </View>
          </View>
          
          <TextInput
            style={[styles.reviewInput, { 
              color: darkMode ? '#ffffff' : '#1f2937',
              backgroundColor: darkMode ? '#4a5568' : '#f3f4f6',
              borderColor: darkMode ? '#4a5568' : '#d1d5db'
            }]}
            placeholder="Share your thoughts about this book..."
            placeholderTextColor={darkMode ? '#a0aec0' : '#6b7280'}
            value={userReview}
            onChangeText={setUserReview}
            multiline
            numberOfLines={6}
            textAlignVertical="top"
          />
          
          <View style={styles.reviewModalActions}>
            <TouchableOpacity
              style={[styles.reviewSubmitButton, !userReview.trim() && styles.reviewSubmitButtonDisabled]}
              onPress={submitReview}
              disabled={!userReview.trim()}
            >
              <Text style={styles.reviewSubmitButtonText}>Submit Review</Text>
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </Modal>
  );

  const renderDownloadOptionsModal = () => (
    <Modal
      visible={showDownloadOptions}
      animationType="slide"
      transparent={true}
      onRequestClose={() => setShowDownloadOptions(false)}
    >
      <TouchableOpacity 
        style={styles.modalOverlay}
        activeOpacity={1}
        onPress={() => setShowDownloadOptions(false)}
      >
        <View style={[styles.downloadModalContent, { backgroundColor: darkMode ? '#2d3748' : '#ffffff' }]}>
          <Text style={[styles.downloadModalTitle, { color: darkMode ? '#ffffff' : '#1f2937' }]}>
            Download Format
          </Text>
          
          <TouchableOpacity
            style={[styles.downloadOption, { backgroundColor: darkMode ? '#4a5568' : '#f3f4f6' }]}
            onPress={() => downloadBook('pdf')}
          >
            <MaterialCommunityIcons name="file-pdf-box" size={24} color="#ef4444" />
            <View style={styles.downloadOptionInfo}>
              <Text style={[styles.downloadOptionTitle, { color: darkMode ? '#ffffff' : '#1f2937' }]}>
                PDF Format
              </Text>
              <Text style={[styles.downloadOptionDesc, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
                Best for reading on computers and tablets
              </Text>
            </View>
            <MaterialCommunityIcons name="download" size={24} color="#667eea" />
          </TouchableOpacity>
          
          <TouchableOpacity
            style={[styles.downloadOption, { backgroundColor: darkMode ? '#4a5568' : '#f3f4f6' }]}
            onPress={() => downloadBook('epub')}
          >
            <MaterialCommunityIcons name="book-open-variant" size={24} color="#667eea" />
            <View style={styles.downloadOptionInfo}>
              <Text style={[styles.downloadOptionTitle, { color: darkMode ? '#ffffff' : '#1f2937' }]}>
                EPUB Format
              </Text>
              <Text style={[styles.downloadOptionDesc, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
                Best for e-readers and mobile devices
              </Text>
            </View>
            <MaterialCommunityIcons name="download" size={24} color="#667eea" />
          </TouchableOpacity>
          
          <TouchableOpacity
            style={[styles.downloadOption, { backgroundColor: darkMode ? '#4a5568' : '#f3f4f6' }]}
            onPress={() => downloadBook('mobi')}
          >
            <MaterialCommunityIcons name="tablet" size={24} color="#f59e0b" />
            <View style={styles.downloadOptionInfo}>
              <Text style={[styles.downloadOptionTitle, { color: darkMode ? '#ffffff' : '#1f2937' }]}>
                MOBI Format
              </Text>
              <Text style={[styles.downloadOptionDesc, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
                Compatible with Amazon Kindle
              </Text>
            </View>
            <MaterialCommunityIcons name="download" size={24} color="#667eea" />
          </TouchableOpacity>
          
          <TouchableOpacity
            style={styles.downloadCancelButton}
            onPress={() => setShowDownloadOptions(false)}
          >
            <Text style={styles.downloadCancelText}>Cancel</Text>
          </TouchableOpacity>
        </View>
      </TouchableOpacity>
    </Modal>
  );

  const styles = StyleSheet.create({
    safeArea: {
      flex: 1,
      backgroundColor: '#667eea',
    },
    container: {
      flex: 1,
      backgroundColor: '#f0f4f8',
    },
    loadingContainer: {
      flex: 1,
      justifyContent: 'center',
      alignItems: 'center',
    },
    loadingText: {
      fontSize: 18,
      fontWeight: '600',
      marginTop: 16,
    },
    header: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      paddingHorizontal: 16,
      paddingVertical: 12,
      paddingTop: Platform.OS === 'ios' ? 40 : 12,
    },
    backButton: {
      padding: 8,
    },
    headerTitle: {
      fontSize: 20,
      fontWeight: 'bold',
      color: '#ffffff',
      flex: 1,
      textAlign: 'center',
      marginHorizontal: 16,
    },
    headerActions: {
      flexDirection: 'row',
      alignItems: 'center',
    },
    iconButton: {
      padding: 8,
      marginLeft: 8,
    },
    scrollView: {
      flex: 1,
    },
    scrollContent: {
      paddingBottom: 100,
    },
    bookCoverContainer: {
      marginHorizontal: 20,
      marginTop: 20,
      marginBottom: 10,
      borderRadius: 20,
      overflow: 'hidden',
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 8 },
      shadowOpacity: 0.25,
      shadowRadius: 12,
      elevation: 8,
    },
    bookCoverGradient: {
      padding: 30,
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: 200,
    },
    bookCoverContent: {
      alignItems: 'center',
    },
    bookCoverTitle: {
      fontSize: 22,
      fontWeight: 'bold',
      color: '#ffffff',
      textAlign: 'center',
      marginTop: 16,
      marginBottom: 8,
      textShadowColor: 'rgba(0, 0, 0, 0.3)',
      textShadowOffset: { width: 1, height: 1 },
      textShadowRadius: 3,
    },
    bookCoverAuthor: {
      fontSize: 16,
      color: 'rgba(255, 255, 255, 0.9)',
      textAlign: 'center',
    },
    bookCoverImage: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      opacity: 0.2,
    },
    bookInfoContainer: {
      marginHorizontal: 20,
      marginBottom: 16,
      padding: 20,
      borderRadius: 16,
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 4 },
      shadowOpacity: 0.1,
      shadowRadius: 8,
      elevation: 4,
    },
    bookTitle: {
      fontSize: 24,
      fontWeight: 'bold',
      marginBottom: 4,
    },
    bookAuthor: {
      fontSize: 16,
      marginBottom: 16,
      fontStyle: 'italic',
    },
    bookMeta: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      marginBottom: 16,
    },
    metaItem: {
      flexDirection: 'row',
      alignItems: 'center',
    },
    metaText: {
      fontSize: 14,
      marginLeft: 6,
    },
    ratingContainer: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: 16,
    },
    ratingStars: {
      flexDirection: 'row',
      alignItems: 'center',
    },
    ratingText: {
      fontSize: 16,
      fontWeight: '600',
      marginLeft: 8,
    },
    priceContainer: {
      flexDirection: 'row',
      alignItems: 'center',
      backgroundColor: '#d1fae5',
      paddingHorizontal: 12,
      paddingVertical: 6,
      borderRadius: 20,
    },
    priceText: {
      fontSize: 16,
      fontWeight: 'bold',
      color: '#065f46',
      marginLeft: 6,
    },
    isbnContainer: {
      flexDirection: 'row',
      alignItems: 'center',
    },
    isbnLabel: {
      fontSize: 14,
      marginRight: 6,
    },
    isbnValue: {
      fontSize: 14,
      fontWeight: '600',
    },
    descriptionContainer: {
      marginHorizontal: 20,
      marginBottom: 16,
      padding: 20,
      borderRadius: 16,
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 4 },
      shadowOpacity: 0.1,
      shadowRadius: 8,
      elevation: 4,
    },
    sectionTitle: {
      fontSize: 18,
      fontWeight: 'bold',
      marginBottom: 12,
    },
    descriptionText: {
      fontSize: 15,
      lineHeight: 24,
    },
    readMoreText: {
      color: '#667eea',
      fontSize: 14,
      fontWeight: '600',
      marginTop: 8,
    },
    topicsContainer: {
      marginHorizontal: 20,
      marginBottom: 16,
      padding: 20,
      borderRadius: 16,
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 4 },
      shadowOpacity: 0.1,
      shadowRadius: 8,
      elevation: 4,
    },
    topicsList: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      marginTop: 8,
    },
    topicTag: {
      paddingHorizontal: 12,
      paddingVertical: 6,
      borderRadius: 16,
      marginRight: 8,
      marginBottom: 8,
    },
    topicText: {
      fontSize: 14,
    },
    chaptersContainer: {
      marginHorizontal: 20,
      marginBottom: 16,
      padding: 20,
      borderRadius: 16,
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 4 },
      shadowOpacity: 0.1,
      shadowRadius: 8,
      elevation: 4,
    },
    chaptersSubtitle: {
      fontSize: 14,
      marginBottom: 16,
    },
    chapterItem: {
      flexDirection: 'row',
      alignItems: 'center',
      padding: 16,
      borderRadius: 12,
      marginBottom: 12,
    },
    chapterNumber: {
      width: 40,
      height: 40,
      borderRadius: 20,
      backgroundColor: '#667eea',
      justifyContent: 'center',
      alignItems: 'center',
      marginRight: 16,
    },
    chapterNumberText: {
      fontSize: 16,
      fontWeight: 'bold',
      color: '#ffffff',
    },
    chapterContent: {
      flex: 1,
    },
    chapterTitle: {
      fontSize: 16,
      fontWeight: '600',
      marginBottom: 4,
    },
    chapterPages: {
      fontSize: 12,
    },
    viewAllChapters: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      paddingVertical: 12,
      marginTop: 8,
    },
    viewAllText: {
      fontSize: 14,
      color: '#667eea',
      fontWeight: '600',
      marginRight: 4,
    },
    ratingSection: {
      marginHorizontal: 20,
      marginBottom: 16,
      padding: 20,
      borderRadius: 16,
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 4 },
      shadowOpacity: 0.1,
      shadowRadius: 8,
      elevation: 4,
    },
    userRatingContainer: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      marginBottom: 20,
    },
    starsContainer: {
      flexDirection: 'row',
    },
    ratingValue: {
      fontSize: 24,
      fontWeight: 'bold',
    },
    reviewButton: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: '#667eea',
      paddingVertical: 12,
      borderRadius: 8,
    },
    reviewButtonText: {
      color: '#ffffff',
      fontSize: 16,
      fontWeight: '600',
      marginLeft: 8,
    },
    reviewsContainer: {
      marginHorizontal: 20,
      marginBottom: 16,
      padding: 20,
      borderRadius: 16,
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 4 },
      shadowOpacity: 0.1,
      shadowRadius: 8,
      elevation: 4,
    },
    reviewsHeader: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: 16,
    },
    seeAllText: {
      color: '#667eea',
      fontSize: 14,
      fontWeight: '600',
    },
    reviewItem: {
      padding: 16,
      borderRadius: 12,
      marginBottom: 12,
    },
    reviewHeader: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      marginBottom: 12,
    },
    reviewerInfo: {
      flex: 1,
    },
    reviewerName: {
      fontSize: 16,
      fontWeight: '600',
      marginBottom: 4,
    },
    reviewStars: {
      flexDirection: 'row',
    },
    reviewDate: {
      fontSize: 12,
    },
    reviewText: {
      fontSize: 14,
      lineHeight: 20,
    },
    noReviews: {
      alignItems: 'center',
      paddingVertical: 32,
    },
    noReviewsText: {
      fontSize: 14,
      textAlign: 'center',
      marginTop: 12,
    },
    actionButtonsContainer: {
      position: 'absolute',
      bottom: 0,
      left: 0,
      right: 0,
      flexDirection: 'row',
      paddingHorizontal: 20,
      paddingVertical: 16,
      paddingBottom: Platform.OS === 'ios' ? 30 : 16,
      borderTopWidth: 1,
      borderTopColor: '#e5e7eb',
      shadowColor: '#000',
      shadowOffset: { width: 0, height: -4 },
      shadowOpacity: 0.1,
      shadowRadius: 8,
      elevation: 8,
    },
    actionButton: {
      flex: 1,
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      paddingVertical: 14,
      borderRadius: 12,
      marginHorizontal: 4,
    },
    previewButton: {
      backgroundColor: '#667eea',
    },
    amazonButton: {
      backgroundColor: '#FF9900',
    },
    downloadButton: {
      backgroundColor: '#10b981',
    },
    actionButtonText: {
      color: '#ffffff',
      fontSize: 14,
      fontWeight: '600',
      marginLeft: 8,
    },
    modalContainer: {
      flex: 1,
    },
    modalHeader: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      paddingHorizontal: 20,
      paddingVertical: 16,
      paddingTop: Platform.OS === 'ios' ? 40 : 16,
    },
    modalTitle: {
      fontSize: 18,
      fontWeight: 'bold',
    },
    previewWebView: {
      flex: 1,
    },
    previewActions: {
      flexDirection: 'row',
      justifyContent: 'space-around',
      paddingVertical: 12,
    },
    previewActionButton: {
      alignItems: 'center',
    },
    previewActionText: {
      fontSize: 12,
      fontWeight: '600',
      marginTop: 4,
    },
    modalOverlay: {
      flex: 1,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      justifyContent: 'flex-end',
    },
    reviewModalContent: {
      borderTopLeftRadius: 20,
      borderTopRightRadius: 20,
      padding: 20,
      maxHeight: height * 0.8,
    },
    reviewModalHeader: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: 20,
    },
    reviewModalTitle: {
      fontSize: 20,
      fontWeight: 'bold',
    },
    reviewModalRating: {
      marginBottom: 20,
    },
    reviewModalLabel: {
      fontSize: 16,
      marginBottom: 12,
    },
    reviewModalStars: {
      flexDirection: 'row',
      justifyContent: 'center',
    },
    reviewInput: {
      borderWidth: 1,
      borderRadius: 12,
      padding: 16,
      fontSize: 16,
      minHeight: 120,
      textAlignVertical: 'top',
      marginBottom: 20,
    },
    reviewModalActions: {
      flexDirection: 'row',
      justifyContent: 'center',
    },
    reviewSubmitButton: {
      backgroundColor: '#667eea',
      paddingHorizontal: 32,
      paddingVertical: 14,
      borderRadius: 12,
      flex: 1,
    },
    reviewSubmitButtonDisabled: {
      backgroundColor: '#9ca3af',
    },
    reviewSubmitButtonText: {
      color: '#ffffff',
      fontSize: 16,
      fontWeight: '600',
      textAlign: 'center',
    },
    downloadModalContent: {
      borderTopLeftRadius: 20,
      borderTopRightRadius: 20,
      padding: 20,
    },
    downloadModalTitle: {
      fontSize: 20,
      fontWeight: 'bold',
      marginBottom: 20,
      textAlign: 'center',
    },
    downloadOption: {
      flexDirection: 'row',
      alignItems: 'center',
      padding: 16,
      borderRadius: 12,
      marginBottom: 12,
    },
    downloadOptionInfo: {
      flex: 1,
      marginHorizontal: 16,
    },
    downloadOptionTitle: {
      fontSize: 16,
      fontWeight: '600',
      marginBottom: 4,
    },
    downloadOptionDesc: {
      fontSize: 12,
      lineHeight: 16,
    },
    downloadCancelButton: {
      paddingVertical: 16,
      alignItems: 'center',
      marginTop: 8,
    },
    downloadCancelText: {
      color: '#ef4444',
      fontSize: 16,
      fontWeight: '600',
    },
  });

  if (loading) {
    return renderLoading();
  }

  return (
    <SafeAreaView style={styles.safeArea}>
      <StatusBar 
        barStyle="light-content" 
        backgroundColor="#667eea" 
      />
      
      {renderHeader()}
      
      <ScrollView
        ref={scrollViewRef}
        style={styles.scrollView}
        contentContainerStyle={styles.scrollContent}
        showsVerticalScrollIndicator={false}
      >
        {renderBookCover()}
        {renderBookInfo()}
        {renderDescription()}
        {renderTopics()}
        {renderChapters()}
        {renderUserRating()}
        {renderReviews()}
        
        {/* Spacer for action buttons */}
        <View style={{ height: 80 }} />
      </ScrollView>
      
      {renderActionButtons()}
      {renderPreviewModal()}
      {renderReviewModal()}
      {renderDownloadOptionsModal()}
    </SafeAreaView>
  );
};

export default BookPreview;